/**
 * Guest roster for the Voices of Impact NY networking directory
 * (pages/VoicesOfImpactNetworkingPage.tsx).
 *
 * Guests are listed only with information they have agreed to share publicly.
 * Email addresses are intentionally NOT stored here — introduction requests
 * are routed through the Association instead, so no inbox is exposed on the
 * page or in the shipped source.
 *
 * To add a guest, append an object below. `sector` doubles as the filter chip
 * label, so reuse an existing value where possible.
 */
export interface NetworkingGuest {
  /** Stable id used as the React key and in introduction requests. */
  id: string
  name: string
  /** Professional headline shown above the title, e.g. 'Sales & Marketing Executive'. */
  headline?: string
  /** Role or title, e.g. 'Vice President, Inclusion'. */
  title?: string
  /** Employer or venture. Optional for independent professionals. */
  organization?: string
  /**
   * For guests holding several positions: each role renders as its own
   * title + organization pair, replacing the single title/organization lines.
   */
  roles?: { title: string; organization: string }[]
  /** City, ST — helps guests find people in their region. */
  location?: string
  /** Filter category, e.g. 'Media', 'Government', 'Business'. */
  sector: string
  /** An additional industry tag; the guest also matches this filter. */
  secondarySector?: string
  /** Further industry tags beyond the secondary one; also matched by filters. */
  extraSectors?: string[]
  /** Two or three sentences in their own voice where available. */
  bio?: string
  /** What they are hoping to find in the room. Shown as a callout. */
  lookingFor?: string
  /** Headshot URL. Falls back to initials when omitted. */
  photo?: string
  /** Optional zoom into the portrait, e.g. 1.3. */
  photoZoom?: number
  /** Point the zoom centers on (CSS transform-origin), e.g. '55% 30%'. */
  photoFocus?: string
  /** Optional CSS color adjustment, e.g. 'saturate(1.1) contrast(1.05)'. */
  photoFilter?: string
  linkedin?: string
  website?: string
  /** An article or press feature about this guest. */
  feature?: string
  /** Extra websites, each shown as its own labeled link chip. */
  additionalLinks?: { label: string; url: string }[]
  /**
   * Rank among the HAA Board of Directors / officers. Present only for board
   * members, who are listed ahead of everyone else; everyone else is grouped
   * by industry (`sector`). Mirrors the order in data/leaders.ts.
   */
  boardOrder?: number
  /**
   * Rank among the evening's featured guests. Listed directly after the HAA
   * board and ahead of the industry groups.
   */
  featuredOrder?: number
  /** Guest id this card should appear directly after in the directory. */
  placeAfter?: string
  /** Set false to hide the introduction button for this guest. */
  acceptsIntros?: boolean
}

export const NETWORKING_GUESTS: NetworkingGuest[] = [
  // ——— Association leadership ———
  {
    id: 'jay-miranda',
    name: 'Jay Miranda',
    boardOrder: 1,
    title: 'Founder, President & CEO',
    organization: 'Honduran-American Association',
    location: 'Washington, DC',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/jay-miranda-96209664/',
    website: 'https://www.hondurasusa.org',
    photo:
      'https://cdn.magicpatterns.com/uploads/5dU2KNEdVenrvDnBdcm72s/Jay_Miranda.png',
  },
  {
    id: 'eva-callejas',
    name: 'Eva Callejas',
    boardOrder: 6,
    title: 'Vice President',
    organization: 'Honduran-American Association',
    location: 'Miami, FL',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/eva-callejas/',
    website: 'https://www.hondurasusa.org',
    photo:
      'https://cdn.magicpatterns.com/uploads/mDbp6cgQYMW1v7L948cYU3/Eva_Callejas.png',
  },

  // ——— Media ———
  {
    id: 'jose-zuniga',
    name: 'José Zuñiga',
    featuredOrder: 2,
    title: 'Actor / Film & Television',
    location: 'New York, NY',
    sector: 'Media',
    website: 'https://www.imdb.com/name/nm0959242/',
    photo:
      'https://cdn.magicpatterns.com/uploads/1oRBp4Kj2kf1HtDSSdVDoB/Jose_Zuniga.png',
  },
  {
    id: 'copan-alvarez',
    name: 'Copán Álvarez',
    featuredOrder: 3,
    title: 'National Sports Anchor',
    organization: 'Telemundo',
    location: 'Miami, FL',
    sector: 'Media',
    linkedin: 'https://www.linkedin.com/in/copan-alvarez-20117063/',
    photo:
      'https://cdn.magicpatterns.com/uploads/bCUC6kHKksfM9MKFSLEfkM/Copan_Alvarez.png',
  },
  {
    id: 'neida-sandoval',
    name: 'Neida Sandoval',
    featuredOrder: 1,
    title: 'Founder & Owner',
    organization: 'Neida Sandoval TV',
    location: 'Doral, FL',
    sector: 'Media',
    linkedin: 'https://www.linkedin.com/in/neida-sandoval-34603b49/',
    photo:
      'https://cdn.magicpatterns.com/uploads/stBj1BR5KXPsKb54xTpGth/Neida_Sandoval.jpg',
  },
  {
    id: 'dunia-elvir',
    name: 'Dunia Elvir',
    featuredOrder: 1.5,
    title: 'President',
    organization: 'National Association of Hispanic Journalists',
    location: 'Los Angeles, CA',
    sector: 'Media',
    linkedin: 'https://www.linkedin.com/in/dunia-elvir-mba-aa313826/',
    feature:
      'https://nahj.org/meet-nahjs-new-president-journalist-and-anchor-dunia-elvir/',
    photo:
      'https://cdn.magicpatterns.com/uploads/1NHxK9rJ7iHVJXmekV8aU5/Dunia_Elvir.png',
  },
  {
    id: 'alex-lainez',
    name: 'Alex Lainez',
    featuredOrder: 16,
    title: 'Host, Pasaporte',
    organization: 'Televicentro',
    location: 'Honduras',
    sector: 'Media',
    website: 'https://www.instagram.com/alexlainez_hn/',
    photo:
      'https://cdn.magicpatterns.com/uploads/tWHkcjyopyTKVd8EhofYYq/Alex_Lainez.png',
  },
  {
    id: 'salvador-mendoza',
    name: 'Salvador Mendoza',
    boardOrder: 5,
    title: 'Vice President, Inclusion',
    organization: 'NBCUniversal',
    roles: [
      { title: 'Vice President, Inclusion', organization: 'NBCUniversal' },
      {
        title: 'Founding Board Member',
        organization: 'Honduran-American Association',
      },
    ],
    location: 'New York, NY',
    sector: 'Media',
    linkedin: 'https://www.linkedin.com/in/salmendoza/',
    photo:
      'https://cdn.magicpatterns.com/uploads/t53uKdTJmAe5A85fYV1nUy/Sal_Mendoza.png',
  },

  // ——— Arts & culture ———
  {
    id: 'walter-krochmal',
    name: 'Walter Krochmal',
    title: 'Executive Director',
    organization: 'Bronx World Film',
    location: 'New York, NY',
    sector: 'Arts & Culture',
    linkedin: 'https://www.linkedin.com/in/walter-l-krochmal-114938a/',
    photo:
      'https://cdn.magicpatterns.com/uploads/doEWFDMCxqc5pSLyDwesQA/Walter_Krochmal.png',
  },
  {
    id: 'thurito-martinez',
    name: 'Thurito Martinez',
    title: 'Public Relations Specialist',
    organization: 'Thurito Martinez Enterprises',
    location: 'New York, NY',
    sector: 'Arts & Culture',
    linkedin: 'https://www.linkedin.com/in/thurito-martinez-a0b421a1/',
    photo:
      'https://cdn.magicpatterns.com/uploads/rUDoks4yhZ8wT8hkHhXJbb/Thurito_Martinez.png',
  },
  {
    id: 'aliene-cochran',
    name: 'Aliene Cochran',
    title:
      'Voice-Over & Dubbing Talent · Actor · Performing Arts Instructor',
    organization: 'AC Performing Arts',
    location: 'Miami, FL',
    sector: 'Arts & Culture',
    linkedin: 'https://www.linkedin.com/in/ali-cochran-36237327b/',
    photo:
      'https://cdn.magicpatterns.com/uploads/nB375pRZWiPSRJibhq8C8Y/Aliene_Cochran.jpg',
  },

  // ——— Business ———
  {
    id: 'maribel-lieberman',
    name: 'Maribel Lieberman',
    featuredOrder: 4,
    title: 'Founder & Owner',
    organization: 'MarieBelle New York Chocolates',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/maribel-lieberman-aa422a20/',
    website: 'https://mariebelle.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/bVfkoyyictgdCowpoPjSdT/Maribel_Lieberman.png',
  },
  {
    id: 'mario-ramos',
    name: 'Mario Ramos',
    boardOrder: 3,
    title: 'Founder & Owner',
    organization: 'Cabezahueca Films',
    roles: [
      { title: 'Founder & Owner', organization: 'Cabezahueca Films' },
      {
        title: 'Founding Board Member',
        organization: 'Honduran-American Association',
      },
    ],
    location: 'Washington, DC',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/mario-ramos-61b67827/',
    website: 'https://www.cabezahuecafilms.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/n9HsWTCYfMbszbLWWs9Ay3/Mario_Ramos.png',
  },
  {
    id: 'carlos-campos',
    name: 'Carlos Campos',
    boardOrder: 4,
    title: 'Founder & Owner',
    organization: 'Carlos Campos New York',
    roles: [
      { title: 'Founder & Owner', organization: 'Carlos Campos New York' },
      {
        title: 'Founding Board Member',
        organization: 'Honduran-American Association',
      },
    ],
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/carlos-campos-9b82a420/',
    website: 'https://carloscampos.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/rfT2px1TUXe2aL1oKJJNoU/Carlos_Campos.png',
  },
  {
    id: 'martha-hernandez',
    name: 'Martha Hernandez',
    title: 'Founder & Owner',
    organization: 'Extreme Woodworks',
    location: 'Miami, FL',
    sector: 'Business',
    secondarySector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/martha-hernandez-1260202a/',
    website: 'https://extremewoodworks.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/7DaPXUA8YwM9L38LKphjJJ/Martha_Hernandez.png',
  },
  {
    id: 'jenny-mains',
    name: 'Dr. Jenny Mains',
    featuredOrder: 5,
    title: 'Special Advisor to the Mayor',
    organization: 'City of New Orleans',
    secondarySector: 'Government',
    extraSectors: ['Nonprofit'],
    roles: [
      {
        title: 'Special Advisor to the Mayor',
        organization:
          'International Partnerships & Strategic Economic Development, City of New Orleans',
      },
      { title: 'Owner & CCO', organization: 'CRC Companies' },
      { title: 'Vice President', organization: 'CRC We Care We Share Foundation' },
    ],
    location: 'New Orleans, LA',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/jenny-mains-md-bb0738142/',
    website: 'https://crc.global/',
    additionalLinks: [
      { label: 'CRC We Care We Share', url: 'https://www.crcwecareweshare.org/' },
    ],
    photo:
      'https://cdn.magicpatterns.com/uploads/rKbPSPTXmPUjAoRXGz2Sbo/Jenny_Mains.jpg',
  },
  {
    id: 'bryan-giron',
    name: 'Bryan Girón',
    featuredOrder: 10,
    title: 'Owner',
    organization: 'Perquín Restaurant',
    location: 'New York, NY',
    sector: 'Business',
    website: 'https://perquinjc.com/#about',
    photo:
      'https://cdn.magicpatterns.com/uploads/aRxykLYtXCnv9RjaxuHaYM/Bryan_Giron.jpg',
  },
  {
    id: 'alvaro-castillo',
    name: 'Alvaro Castillo',
    featuredOrder: 21,
    title: 'Manager, Global Government Affairs & Public Policy',
    organization: 'Warner Bros. Discovery',
    roles: [
      {
        title: 'Manager, Global Government Affairs & Public Policy',
        organization: 'Warner Bros. Discovery',
      },
      { title: 'President', organization: 'Espacio H' },
    ],
    location: 'Washington, DC',
    sector: 'Nonprofit',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/alvaro-castillo-72103b9b/',
    website: 'https://www.espaciohnd.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/4t8JLzrE3U1v3tkjqGBLWH/Alvaro_Castillo.png',
  },
  {
    id: 'cindy-mcfield-asamoah',
    name: 'Cindy McField-Asamoah',
    title: 'Founder & CEO',
    organization: 'The Boys Room · Citi Mosaic Realty',
    location: 'Brooklyn, NY',
    sector: 'Nonprofit',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/cindy-mcfield-asamoah-80823bab/',
    website: 'https://www.theboysroom.org',
    photo:
      'https://cdn.magicpatterns.com/uploads/u5BnEvWxq1sk5h4tKbtKjY/Cindy_McField-Asamoah.jpg',
  },
  {
    id: 'kobla-asamoah',
    name: 'Kobla Asamoah',
    title: 'Senior Director',
    organization: 'Caminus Ventures',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/kobla-asamoah/',
    photo:
      'https://cdn.magicpatterns.com/uploads/nM6mfzkQgq3XmQxMaGPoS9/Kobla_Asamoah.png',
  },
  {
    id: 'cinthia-lopez',
    name: 'Cinthia López',
    featuredOrder: 7,
    title: 'Director, People Operations — Americas Talent Outreach & Programs',
    organization: 'Google',
    location: 'Washington, DC',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/cinthia-l%C3%B3pez-262560a6/',
    photo:
      'https://cdn.magicpatterns.com/uploads/sMexGFJX85PFDvDhkoERBJ/Cinthia_Lopez.png',
  },
  {
    id: 'silvia-alvarez',
    name: 'Silvia Alvarez',
    title: 'Director of Communications & External Affairs',
    organization: 'Major League Baseball',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/silvia-alvarez-8770865/',
    photo:
      'https://cdn.magicpatterns.com/uploads/4KTatShRYwcAiFGV5jF3wL/Silvia_Alvarez.jpg',
  },
  {
    id: 'rose-anne-tifre',
    name: 'Rose-Anne Tifre',
    featuredOrder: 22,
    headline: 'Sales & Marketing Executive',
    title: 'CEO',
    organization: 'Brand Team Inc.',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/rose-anne-tifre-41b682/',
    photo:
      'https://cdn.magicpatterns.com/uploads/7vGr683q4kfeRmWdQCJKWB/Rose-Anne_Tifre.jpg',
  },
  {
    id: 'luis-carlo-bulnes',
    name: 'Luis Carlo Bulnes',
    title: 'Independent Research Consultant',
    organization: 'Cognitive & Affective Neuroscience',
    location: 'Bronx, NY',
    sector: 'Nonprofit',
    secondarySector: 'Health',
    linkedin: 'https://www.linkedin.com/in/luiscarlobulnes/',
    photo:
      'https://cdn.magicpatterns.com/uploads/6KHNcyt3g5xHq7mt36yJkf/Luis_Carlo_Bulnes.png',
  },

  {
    id: 'daniel-salcedo-media',
    name: 'Daniel Salcedo',
    title: 'Head of Branded Content & CTV Distribution',
    organization: 'The Mediam Group',
    location: 'New York, NY',
    sector: 'Media',
    linkedin: 'https://www.linkedin.com/in/danielsalcedo/',
    website: 'https://themediamgroup.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/pxMqaafyUzmecoeiz5kgov/Daniel_Salcedo.jpg',
  },

  // ——— Nonprofit & diaspora organizations ———
  {
    id: 'joel-betancourth',
    name: 'Joel Betancourth',
    title: 'CEO & Broker · President & Founder',
    organization:
      'JB Real Estate Group LLC · Hondurans in Action USA · Diáspora Organizada de Hondureños DFW',
    location: 'Dallas, TX',
    sector: 'Nonprofit',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/joel-betancourth-5a020784/',
    photo:
      'https://cdn.magicpatterns.com/uploads/5TJ2H8TQdkx2M1hCLwyLVx/Joel_Betancourth.jpg',
  },
  {
    id: 'arnaldo-ugarte',
    name: 'Arnaldo Ugarte',
    title: 'Artist · Sculpture Conservator',
    organization: 'Employer: Rockefeller Fund',
    location: 'Stamford, CT',
    sector: 'Arts & Culture',
    website: 'https://www.rbf.org/people/arnaldo-ugarte',
    photo:
      'https://cdn.magicpatterns.com/uploads/hZ1kV7745f3DDXDb3F8DMF/20240519_190949.jpg',
    photoZoom: 1.8,
    photoFocus: '51% 45%',
    photoFilter: 'saturate(1.12) contrast(1.06) brightness(1.03)',
  },
  {
    id: 'alison-wabeke',
    name: 'Alison Wabeke',
    title: 'Deputy Executive Director',
    organization: 'ASJ-US',
    location: 'Grand Rapids, MI',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/alison-wabeke-derooy-1337882a/',
    website: 'https://www.asj-us.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/dPHR16DrcNfMdNvN6ncDEw/Alison_Wabeke.png',
  },
  {
    id: 'sindy-benavides',
    name: 'Sindy Benavides',
    boardOrder: 2,
    title: 'CEO',
    organization: 'AQUI — The Accountability Movement',
    roles: [
      { title: 'CEO', organization: 'AQUI — The Accountability Movement' },
      { title: 'Co-Founder', organization: 'Honduran-American Association' },
    ],
    location: 'Lorton, VA',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/sindy-benavides/',
    website: 'https://www.aqui.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/p2G3G7NYL7tRSjxxQKgvBm/Sindy_Benavides.png',
  },
  {
    id: 'jose-avila',
    name: 'José Francisco Ávila',
    featuredOrder: 9,
    title: 'President & Founder',
    organization: 'Garifuna Coalition USA',
    location: 'New York, NY',
    sector: 'Nonprofit',
    linkedin:
      'https://www.linkedin.com/in/jos%C3%A9-francisco-avila-a9557615/',
    website: 'https://garifunacoalition.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/mwhqk2VFoRaE7efiXXKDCx/Jose_Avila.jpg',
  },
  {
    id: 'jose-equigure',
    name: 'Jose Antonio Eguigure Castillo',
    title: 'Co-Founder & Managing Partner',
    organization: 'ARA Policy & Management Group',
    location: 'Boston, MA',
    sector: 'Nonprofit',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/joseeguigure/',
    website: 'https://arapmg.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/5jtGkeUSenyzxGF7mwdiCV/Jose_Eguigure.jpg',
  },
  {
    id: 'katia-gomez',
    name: 'Katia Gomez',
    featuredOrder: 18,
    title: 'Executive Director',
    organization: 'Educate 2 Envision',
    location: 'Oakland, CA',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/katiagomez1/',
    website: 'https://www.educate2envision.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/mw1PSoVh6W7qn3ywoXN3XY/Katia_Gomez.jpg',
  },
  {
    id: 'sara-brakhane',
    name: 'Sara Brakhane',
    featuredOrder: 20,
    title: 'Executive Director',
    organization: 'Chispa Project',
    location: 'St. Louis, MO',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/sarabrakhane/',
    website: 'https://www.chispaproject.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/eDPP9HbcffswTBxzVQZkYY/Sara_Brakhane.png',
  },
  {
    id: 'thiery-sparks',
    name: 'Thiery Sparks',
    title: 'Operations Coordinator',
    organization: 'Honduras Good Works',
    location: 'Austin, TX',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/thiery-sparks/',
    website: 'https://hondurasgoodworks.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/sJcnD22dXHADvpjUWqSa9P/Thiery_Sparks.png',
  },
  {
    id: 'andrew-castro',
    name: 'Andrew Castro',
    title: 'Executive Director',
    organization: 'Lifting Honduras',
    location: 'New York, NY',
    sector: 'Nonprofit',
    website: 'https://www.liftinghonduras.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/cUDNbbBoD64t2FeZon7AsX/Andrew_Castro.png',
  },
  {
    id: 'maxie-gluckman',
    name: 'Maxie Gluckman',
    featuredOrder: 19,
    title: 'Executive Director',
    organization: 'Friends of Fundesur',
    location: 'San Diego, CA',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/maxiegluckman/',
    website: 'https://friendsoffundesur.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/r26u6G7kNyjSfqxPf8SSfW/Maxie_Gluckman.png',
  },
  {
    id: 'nubia-mendoza',
    name: 'Nubia Mendoza',
    featuredOrder: 17,
    title: 'President',
    organization: 'Fundación de Niños con Cáncer — Honduras',
    location: 'Honduras',
    sector: 'Nonprofit',
    website: 'https://salvamivida.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/ojqdZXafXmcp1kFgSJJbN6/Nubia_Mendoza.jpg',
  },
  {
    id: 'hanany-flores',
    name: 'Hanany Flores',
    title: 'Development Director',
    organization: 'Fundación de Niños con Cáncer — Honduras',
    location: 'Honduras',
    sector: 'Nonprofit',
    website: 'https://salvamivida.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/soc6ecBc3jnUhLULYZA1Z6/Hanany_Flores.png',
  },
  {
    id: 'laura-hortal',
    name: 'Laura Hortal',
    title: 'Director of Engagement',
    organization: 'Carolina Honduras Health Foundation',
    location: 'Winston-Salem, NC',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/laura-h-7523a06/',
    website: 'https://chhf.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/iwb7SebSW8d61WU2AxZV6r/Laura_Hortal.png',
  },
  {
    id: 'alejandro-hortal',
    name: 'Alejandro Hortal',
    title: 'Professor',
    organization: 'Wake Forest University',
    location: 'North Carolina',
    sector: 'Education',
    linkedin:
      'https://www.linkedin.com/in/alejandro-hortal-ph-d-a1a465187/',
    photo:
      'https://cdn.magicpatterns.com/uploads/qJm8rRsFvb9k6onCrbM8Zc/Alejandro_Hortal.jpg',
  },
  {
    id: 'camila-reina',
    name: 'Camila Reina',
    featuredOrder: 19.5,
    title: 'CEO',
    organization: 'Honduras Outreach International',
    location: 'Honduras',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/camila-reina-402a3847/',
    website: 'https://hoi.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/tecVv9r1dG8T5UqTyqGDet/Camila_Reina.png',
  },
  {
    id: 'valeska-daley',
    name: 'Valeska Daley',
    title: 'Executive Director',
    organization: 'Uphams Main Streets in Dorchester',
    location: 'Boston, MA',
    sector: 'Nonprofit',
  },
  {
    id: 'dolly-figueroa-allen',
    name: 'Dolly Figueroa Ayala',
    title: 'Executive Director',
    organization: 'Hondurans in LA',
    location: 'Los Angeles, CA',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/dolly-figueroa-27507157/',
    photo:
      'https://cdn.magicpatterns.com/uploads/7EA5dcH78EcPk67rjEQ2Un/Dolly_Figueroa.png',
  },
  {
    id: 'gloria-castro',
    name: 'Gloria Castro',
    title: 'Board Member',
    organization: 'Boston Honduran Committee',
    location: 'Boston, MA',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/gloria-castro-560505132/',
    photo:
      'https://cdn.magicpatterns.com/uploads/iN6tcCGn23myaB7owy4WVs/Gloria_Castro.png',
  },
  {
    id: 'sharon-keyes',
    name: 'Sharon Keyes',
    title: 'Board Member · Board Chair',
    organization:
      'Boston Honduran Committee · Twelfth Baptist Church Honduras Mission Ministry',
    location: 'Dorchester, MA',
    sector: 'Nonprofit',
  },

  // ——— Government & public service ———
  {
    id: 'irma-rosa-suazo',
    name: 'Irma Rosa Suazo',
    featuredOrder: 13,
    title: 'Deputy Chief of Mission',
    organization: 'Embassy of Honduras in the United States',
    location: 'Washington, DC',
    sector: 'Government',
  },
  {
    id: 'fernando-paz',
    name: 'Fernando Paz',
    featuredOrder: 15,
    title: 'Vice Consul',
    organization: 'Consulate of Honduras — New York',
    location: 'New York, NY',
    sector: 'Government',
    photo:
      'https://cdn.magicpatterns.com/uploads/aguk9rWmK8spzDKMjPLfTY/Fernando_Paz.png',
  },
  {
    id: 'jessy-torres',
    name: 'Jessy Torres',
    featuredOrder: 14,
    title: 'Chief of Consular Mission',
    organization: 'Consulate of Honduras — New York',
    location: 'New York, NY',
    sector: 'Government',
    photo:
      'https://cdn.magicpatterns.com/uploads/dYaEfkPRkjhda46XY1ufGh/Jessy_Torres.jpg',
  },
  {
    id: 'ana-molina',
    name: 'Ana Molina',
    title: 'Consular Officer',
    organization: 'Consulate of Honduras — New York',
    location: 'New York, NY',
    sector: 'Government',
    photo:
      'https://cdn.magicpatterns.com/uploads/9Uhk7Dwhk5kz6YmPQfscbe/Ana_Molina.jpg',
  },
  {
    id: 'lidice-gonzalez',
    name: 'Lídice González',
    featuredOrder: 15.5,
    title: 'Junior Counselor',
    organization: 'Inter-American Development Bank',
    location: 'Washington, DC',
    sector: 'Government',
    linkedin:
      'https://www.linkedin.com/in/l%C3%ADdice-gonz%C3%A1lez-186545187/',
    photo:
      'https://cdn.magicpatterns.com/uploads/e8LXVvAMtHpqQASf7iBZZn/Lidice_Gonzalez.png',
  },
  {
    id: 'erika-hollner',
    name: 'Erika Hollner',
    featuredOrder: 11,
    title: 'Honduras Desk Director',
    organization: 'U.S. Department of State',
    location: 'Washington, DC',
    sector: 'Government',
    linkedin: 'https://www.linkedin.com/in/erika-hollner-42089b23/',
    photo:
      'https://cdn.magicpatterns.com/uploads/eBbW9yBd1mQrhVvDcJEziX/Erika_Hollner.jpg',
  },
  {
    id: 'jose-quiroz',
    name: 'Jose Quiroz',
    featuredOrder: 6,
    title: 'Sheriff',
    organization: "Arlington County Sheriff's Office",
    location: 'Arlington, VA',
    sector: 'Government',
    website:
      'https://www.arlingtonva.us/Government/County-Leadership/Executive-Bios/Jose-Quiroz',
    photo:
      'https://cdn.magicpatterns.com/uploads/qPaiH58b2cQsX16MMZMQXi/Jose_Quiroz.png',
  },

  // ——— Law Enforcement ———
  {
    id: 'stivin-benedith',
    name: 'Stivin Benedith',
    title: 'President',
    organization: 'Garifuna Americans in Law Enforcement Association',
    location: 'New York, NY',
    sector: 'Law Enforcement',
    photo:
      'https://cdn.magicpatterns.com/uploads/d32hqVLeRvNDU9BkcjVKKD/IMG_1442.png',
  },

  // ——— Education ———
  {
    id: 'jesenia-castro-burke',
    name: 'Jesenia Castro-Burke',
    title: 'Director of Diversity, Equity and Inclusion',
    organization:
      'Massachusetts Department of Early Education and Care · Board VP, Boston Honduran Committee',
    location: 'Boston, MA',
    sector: 'Education',
    secondarySector: 'Government',
    linkedin:
      'https://www.linkedin.com/in/jesenia-castro-burke-msw-51a73b71/',
    website: 'https://bhrc.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/nkNebH4NLJZhQrTQ9Jd9kx/Jesenia_Castro-Burke.png',
  },
  {
    id: 'diana-brown',
    name: 'Diana Brown',
    title: 'School Board Member',
    organization: 'Manassas City Public Schools',
    location: 'Washington, DC',
    sector: 'Education',
    linkedin: 'https://www.linkedin.com/in/diana-brown-291107a8/',
    photo:
      'https://cdn.magicpatterns.com/uploads/dcY4rWuEhSFjMLdNSKMnhx/Diana_Brown.png',
  },
  {
    id: 'lourdes-mendoza',
    name: 'Lourdes Mendoza',
    title: 'Professor',
    organization: 'Parsons School of Design',
    location: 'New York, NY',
    sector: 'Education',
    linkedin: 'https://www.linkedin.com/in/lourdes-mendoza-64977713/',
    photo:
      'https://cdn.magicpatterns.com/uploads/rexe4zcMBNbMrs4StMW2Zy/Lourdes_Mendoza.jpg',
  },
  {
    id: 'maria-jose-agurcia-fitzgerald',
    name: 'María José Agurcia Fitzgerald',
    title: 'Author & Educator',
    organization: "Random House Children's Books · The Brearley School",
    location: 'Montvale, NJ',
    sector: 'Education',
    linkedin: 'https://www.linkedin.com/in/maria-jose-fitzgerald-9343786b/',
    website: 'https://mariajosefitzgerald.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/cMTcH7VoYqtbyJ5UH65oTN/Maria_Jose_Agurcia_Fitzgerald.png',
  },
  {
    id: 'edi-corea',
    name: 'Edi Corea',
    title: 'Bilingual Social Studies Educator',
    organization: 'Westbury School District',
    location: 'Westbury, NY',
    sector: 'Education',
    photo:
      'https://cdn.magicpatterns.com/uploads/cJB4yVGTjpGbLnLbpvkuwx/Edi_Corea.jpg',
  },

  // ——— Health & law ———
  {
    id: 'cheryl-aguilar',
    name: 'Dr. Cheryl Aguilar',
    featuredOrder: 6.1,
    title: 'Founder & Director',
    organization: 'Hope Center for Wellness',
    location: 'Washington, DC',
    sector: 'Health',
    linkedin: 'https://www.linkedin.com/in/cherylaguilar/',
    website: 'https://hopecenterforwellness.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/nCreiTUXFM2pEgtxg7fwML/Cheryl_Aguilar.jpg',
  },
  {
    id: 'ana-sierra',
    name: 'Dr. Ana Sierra',
    title: 'Owner & Co-Founder',
    organization: 'Ana Sierra Counseling · Latina Healing Institute',
    location: 'Washington, DC',
    sector: 'Health',
    linkedin: 'https://www.linkedin.com/in/asierracounseling/',
    website: 'https://anasierracounseling.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/i6PbxvU3ZHrkyS98gc6vSZ/Ana_Sierra.jpg',
  },
  {
    id: 'susan-cameron',
    name: 'Dr. Susan Cameron',
    title: 'Manager of Behavioral Health & Wellness, NY Market',
    organization: 'Cityblock Health',
    location: 'New York, NY',
    sector: 'Health',
    linkedin: 'https://www.linkedin.com/in/dr-s-cameron/',
    photo:
      'https://cdn.magicpatterns.com/uploads/bybv5ScyPeTqHrTfyQr2jS/Susan_Cameron.png',
  },
  {
    id: 'jose-lopez',
    name: 'Jose Lopez',
    title: 'Founder & Owner',
    organization: 'Lopez Law Firm',
    location: 'Houston, TX',
    sector: 'Law',
    linkedin: 'https://www.linkedin.com/in/attorneylopeztx/',
    website: 'https://www.jslopez.com/eng/',
    photo:
      'https://cdn.magicpatterns.com/uploads/2qB3CfHk7qraWUhWFbp36X/Jose_Lopez.jpg',
  },
  {
    id: 'lily-maldonado',
    name: 'Lily Maldonado',
    title: 'Founder · Case Manager',
    organization:
      'Lily Maldonado Immigration Services · National Youth Advocate Program',
    location: 'Greenville, SC',
    sector: 'Law',
    linkedin: 'https://www.linkedin.com/in/lily-maldonado-0b8449194/',
    photo:
      'https://cdn.magicpatterns.com/uploads/d4xjc5CZ2VKYTkqtwv1Y7B/Lily_Maldonado.png',
  },
  {
    id: 'kenia-maldonado',
    name: 'Kenia Maldonado',
    title: 'Co-Founder & Owner',
    organization: 'Lily Maldonado Immigration Services',
    location: 'Greenville, SC',
    sector: 'Law',
    photo:
      'https://cdn.magicpatterns.com/uploads/p1ZZNreh8uVFVrCEYnTLSK/Kenia_Maldonado.jpg',
  },
  {
    id: 'maria-caceres-boneau',
    name: 'Maria Caceres-Boneau',
    title: 'Partner & Employment Lawyer',
    organization: 'K&L Gates',
    location: 'New York, NY',
    sector: 'Law',
    linkedin: 'https://www.linkedin.com/in/maria-caceres-boneau/',
    photo:
      'https://cdn.magicpatterns.com/uploads/dPwBtJmqHrAhs3roZsKLYS/Maria_Caceres-Boneau.jpg',
  },

  // ——— Entertainment & creators ———
  {
    id: 'james-martinez',
    name: 'James Martinez',
    title: 'Content Creator',
    organization: 'Digital Content Creator · 176K followers',
    location: 'Bronx, NY',
    sector: 'Entertainment',
    website:
      'https://voyagela.com/interview/rising-stars-meet-james-martinez-of-new-york/',
    photo:
      'https://cdn.magicpatterns.com/uploads/x6Rvk1AU7qN6DKdnSUVMBZ/James_Martinez.jpg',
  },
  {
    id: 'franklin-enrique-garcia',
    name: 'Franklin Enrique Garcia',
    featuredOrder: 6.5,
    title: 'Design Project Lead · RPL, RLA/CCL',
    organization: 'Meta',
    location: 'New Orleans, LA',
    sector: 'Business',
    linkedin:
      'https://www.linkedin.com/in/franklin-enrique-garcia-03762551/',
    website: 'https://www.franklinenriquegarcia.com/about',
    photo:
      'https://cdn.magicpatterns.com/uploads/1kCqSEhz7VmHGEWJa7DGRg/Franklin_Enrique_Garcia.jpg',
  },
  {
    id: 'mary-solis',
    name: 'Mary Solis',
    title: 'Fitness & Nutrition Coach',
    organization: 'Solis Health & Wellness',
    location: 'Bronx, NY',
    sector: 'Health',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/mary-solis-194a538/',
    website: 'https://beacons.ai/iammary_solis',
    photo:
      'https://cdn.magicpatterns.com/uploads/9w5M1oWM2Lath3oDJeU3T4/Mary_Solis.png',
  },
  {
    id: 'samuel-brooks',
    name: 'Samuel Brooks',
    title: 'Executive Director',
    organization: 'Mott Haven Historic District Association',
    location: 'New York, NY',
    sector: 'Nonprofit',
    website: 'https://motthavenhistory.org/home/samuel-brooks/',
    photo:
      'https://cdn.magicpatterns.com/uploads/1nNki8SFsh71VJX58aCcGR/Samuel_Brooks.png',
  },
  {
    id: 'hector-urbina',
    name: 'Dr. Hector Manuel Urbina',
    title: 'National Director of Programming',
    organization: 'National Youth Advocate Program',
    location: 'Miami, FL',
    sector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/hector-urbina-ph-d-3b97a0158/',
    website: 'https://www.nyap.org/',
    photo:
      'https://cdn.magicpatterns.com/uploads/2cXPM1AuUDULNtD8GmPdMw/Hector_Urbina.jpg',
  },
  {
    id: 'jose-alvarado',
    name: 'Jose Alvarado',
    title: 'Senior Programmer & Producer',
    organization: 'THE OFFICE performing arts + film',
    location: 'Brooklyn, NY',
    sector: 'Business',
    secondarySector: 'Media',
    linkedin: 'https://www.linkedin.com/in/josegiddel/',
    website: 'https://theofficearts.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/wnuaUaK2mKvQd86hFnvkTy/Jose_Alvarado.jpg',
    photoZoom: 1.4,
    photoFocus: '50% 25%',
  },
  {
    id: 'victoria-jenn-rodriguez',
    name: 'Victoria Jenn Rodriguez',
    title: 'CEO',
    organization: 'VJR Enterprises, LLC · I Dare to Leap Academy',
    location: 'Bloomfield, NJ',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/victoriajennrodriguez/',
    website: 'https://victoriajenn.com/',
    photo:
      'https://cdn.magicpatterns.com/uploads/vDJqEWr6isb4oK3pjb2HZe/Victoria_Rodriguez.jpg',
    photoZoom: 1.3,
    photoFocus: '47% 0%',
  },
  {
    id: 'isabelle-montesinos',
    name: 'Isabelle Montesinos',
    title: 'Senior Manager, External Affairs',
    organization: 'Queens Museum',
    location: 'New York, NY',
    sector: 'Arts & Culture',
    linkedin: 'https://www.linkedin.com/in/imr12/',
    photo:
      'https://cdn.magicpatterns.com/uploads/mB5S7AZoz8rRS1Yj9pmp6w/Isabelle_Montesinos.jpg',
  },
  {
    id: 'sandra-rice',
    name: 'Sandra Rice',
    title: 'EVP, Corporate Strategy & Engagement',
    organization: 'Coqual',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/sandradrice/',
    photo:
      'https://cdn.magicpatterns.com/uploads/4PCrURq6ZLMdWQ4arxGPhp/Sandra_Rice.jpg',
  },
  {
    id: 'dorian-langlais',
    name: 'Dorian Langlais',
    title: 'Co-Founder & Managing Partner',
    organization: 'Rally Point Public Relations',
    location: 'New York, NY',
    sector: 'Media',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/dorianlanglais/',
    photo:
      'https://cdn.magicpatterns.com/uploads/1W1mUBXSV26utRYx5eexLf/Dorian_Langlais.jpg',
  },
  {
    id: 'arlenee-lopez-ferguson',
    name: 'Arlenee Lopez-Ferguson',
    title: 'Senior VP & CISO',
    organization: 'PENDULUM',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/arlenee-lopez/',
    photo:
      'https://cdn.magicpatterns.com/uploads/nEFjMv83Gb2tg59KV8xjf7/Arleene_Lopez-Ferguson.png',
  },
  {
    id: 'jemima-canales',
    name: 'Jemima Miranda de Canelas',
    title: 'Clinical Director',
    organization:
      'Perfect Body Image LLC (Widow of Honduran artist Moisés Canelo)',
    location: 'New York, NY',
    sector: 'Business',
    photo:
      'https://cdn.magicpatterns.com/uploads/hR8Mh4ruvhWGsZ5i1CXcg9/Jemima_Canales.png',
  },
  {
    id: 'angelica-urbina',
    name: 'Angelica Urbina',
    title: 'National Foster Parent Training & Professional Support Manager',
    organization: 'National Youth Advocate Program',
    location: 'Miami, FL',
    sector: 'Nonprofit',
    website: 'https://www.nyap.org/',
  },
  {
    id: 'martha-aparicio',
    name: 'Martha Aparicio',
    title: 'President',
    organization: 'La Voz de Honduras',
    location: 'New York, NY',
    sector: 'Media',
    secondarySector: 'Nonprofit',
  },
  {
    id: 'keyla-sanders',
    name: 'Keyla Sanders',
    title: 'Owner · Author of "Mojada"',
    organization: 'Keyla Sanders Photography',
    location: 'Washington, DC',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/keylasanders/',
    photo:
      'https://cdn.magicpatterns.com/uploads/1593bw2eNZRreY77rWAUnF/WhatsApp_Image_2026-09-22_at_21.47.24.jpg',
    photoZoom: 1.35,
    photoFocus: '57% 38%',
  },
  {
    id: 'maynor-cordova',
    name: 'Maynor Cordova',
    title: 'Founder & CEO',
    organization: 'Maynor Cordova Art',
    location: 'Honduras',
    sector: 'Arts & Culture',
    website: 'https://www.facebook.com/maynor.cordova.9',
    photo:
      'https://cdn.magicpatterns.com/uploads/uRc11ZQD3UKWke4ACMCUrZ/Maynor_Cordova.jpg',
  },
  {
    id: 'catherine-soliz-rey',
    name: 'Catherine Ochun Soliz Rey',
    title: 'President',
    organization: 'Wabafu Garifuna Dance Theatre',
    location: 'New York, NY',
    sector: 'Arts & Culture',
    linkedin: 'https://www.linkedin.com/in/catherinesolizrey/',
    photo:
      'https://cdn.magicpatterns.com/uploads/tcJFcjaTiffyxipsrDDWDE/Catherine_Soliz-REy.jpg',
  },
  {
    id: 'carlos-bonilla',
    name: 'Carlos Bonilla',
    title: 'Owner & Founder',
    organization: 'B Square Solutions',
    location: 'Lorton, VA',
    sector: 'Business',
  },
  {
    id: 'gricelda-ramos',
    name: 'Gricelda Ramos',
    title: 'Marketing & AI MBA Student · Former U.S. Diplomat',
    organization: 'Rice University · U.S. Department of State (former)',
    location: 'Houston, TX',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/griceldaramos',
    photo:
      'https://cdn.magicpatterns.com/uploads/nYpVQeaKVsikHFF7zgZRBs/Gricelda_Ramos.png',
  },
  {
    id: 'keila-alvarado',
    name: 'Keila Alvarado',
    title: 'Board Member · Owner · Entertainment Journalist',
    organization:
      'Latinos in Film + Entertainment Org · Flamingle Entertainment',
    location: 'New York, NY',
    sector: 'Media',
    secondarySector: 'Business',
  },
  {
    id: 'shirley-flores',
    name: 'Shirley Flores',
    title: 'Production Assistant',
    organization: 'NBC',
    location: 'Corona, NY',
    sector: 'Media',
  },
  {
    id: 'oscar-flores',
    name: 'Oscar Flores',
    title: 'Production Assistant, NBC News NOW',
    organization: 'NBCUniversal',
    location: 'New York, NY',
    sector: 'Media',
    linkedin: 'https://www.linkedin.com/in/oscar-flores-pavon/',
    photo:
      'https://cdn.magicpatterns.com/uploads/byTLeWor9WNZahBHv3SMtU/Oscar_Flores.jpg',
  },
  {
    id: 'elmer-chavez',
    name: 'Elmer J. Chavez',
    title: 'Vice President',
    organization: 'J.P. Morgan Chase Private Bank',
    location: 'Brooklyn, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/elmer-j-chavez/',
    photo:
      'https://cdn.magicpatterns.com/uploads/hUC5ndFY1aogjYXdNafD47/Elmer_Chavez.jpg',
  },
  {
    id: 'april-tobie',
    name: 'April Tobie',
    title: 'Model & Digital Content Creator',
    location: 'New York, NY',
    sector: 'Entertainment',
    linkedin: 'https://www.linkedin.com/in/april-tobie-7b4630364/',
    photo:
      'https://cdn.magicpatterns.com/uploads/4hugHditByaJuZGzcCFmLn/April_Tobie.jpg',
  },
  {
    id: 'demi-marie-alvarado',
    name: 'Demi Marie Alvarado',
    title: 'Co-Owner',
    organization: 'Coral View Beach Resort — Utila',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/demi-marie-alvarado-977b18377/',
    photo:
      'https://cdn.magicpatterns.com/uploads/nUaVCoowQmtPxTqUEmD9u6/Demi_Alvarado.png',
  },
  {
    id: 'jason-guardado',
    name: 'Jason Guardado',
    title: 'Owner',
    organization: 'Independent Health Consultant',
    location: 'New York, NY',
    sector: 'Health',
    linkedin: 'https://www.linkedin.com/in/jason-guardado-997b308',
  },
  {
    id: 'kenny-salamanca',
    name: 'Kenny Salamanca',
    title: 'DJ · Producer · Audio Engineer',
    organization: 'KONCIERTO Music',
    location: 'New York, NY',
    sector: 'Entertainment',
    website: 'https://www.instagram.com/koncierto',
    feature: 'https://canvasrebel.com/meet-kenny-salamanca-bautista/',
  },
  {
    id: 'luther-lohr',
    name: 'Luther Lohr',
    title: 'Teacher',
    organization: 'NYC Department of Education',
    location: 'New York, NY',
    sector: 'Education',
    linkedin: 'https://www.linkedin.com/in/luther-m-lohr-72229142/',
  },
  {
    id: 'elvia-brito-murillo',
    name: 'Elvia Brito Murillo',
    title: 'Founder & CEO',
    organization: 'Musa Magazine · Musa Foundation',
    location: 'Richmond, VA',
    sector: 'Business',
    website: 'https://www.musamagazine.us',
    photo:
      'https://cdn.magicpatterns.com/uploads/aLySeL18bAdrwvrMaq9Y8e/Elvia_Brito.jpg',
  },
  {
    id: 'brianna-ardon',
    name: 'Brianna Ardon',
    title: 'President',
    organization: 'A&A Safety Resources',
    location: 'Richmond, VA',
    sector: 'Business',
    website: 'https://www.aasafetyresources.com',
    photo:
      'https://cdn.magicpatterns.com/uploads/9y8pkWQYeNUMW8Dvp9BM5e/Briana_Ardon.png',
  },
  {
    id: 'oscar-barahona',
    name: 'Oscar Barahona',
    featuredOrder: 6.2,
    title: 'Artistic Director',
    organization: 'Orquesta Filarmónica de San Pedro Sula',
    location: 'San Pedro Sula, Honduras',
    sector: 'Arts & Culture',
    website: 'https://www.lafil-sps.com/director-art%C3%ADstico',
    photo:
      'https://cdn.magicpatterns.com/uploads/crvZqtRdnMdQ6e85fsHu9s/Oscar_Barahona.png',
  },
  {
    id: 'marco-matute',
    name: 'Marco Matute',
    featuredOrder: 6.25,
    headline: 'World-Renowned Tenor',
    title: 'CEO',
    organization: 'M27, LLC',
    location: 'New York, NY',
    sector: 'Arts & Culture',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/marcomatute/',
    photo:
      'https://cdn.magicpatterns.com/uploads/h38E27ZBYsc6V7eB3Xo4h2/Marco_Matute.jpg',
  },
  {
    id: 'maurice-jackson',
    name: 'Maurice Jackson',
    title: 'Director',
    organization: 'Fortis Lux Financial',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/maujackson/',
    photo:
      'https://cdn.magicpatterns.com/uploads/as6YaoRasVK4zb3AEhCFUn/Maurice_Jackson.jpg',
  },
  {
    id: 'yudy-canelas',
    name: 'Yudy Canelas',
    title: 'Fulbright Scholar · Junior Finance Specialist',
    organization: 'Clark University · Leadership Mission International',
    location: 'Worcester, MA',
    sector: 'Education',
    secondarySector: 'Business',
    linkedin: 'https://www.linkedin.com/in/yudy-canelas-a6b252184/',
    photo:
      'https://cdn.magicpatterns.com/uploads/6jSFcPcotvZzXi9U2iNGzy/Yudy_Canelas.png',
  },
  {
    id: 'haley-amaya',
    name: 'Haley Amaya',
    featuredOrder: 16.5,
    title: 'Community Leader & Model',
    organization: 'Cultura Cinco Estrellas · Miss Grand Comunidad USA',
    location: 'Los Angeles, CA',
    sector: 'Entertainment',
    secondarySector: 'Nonprofit',
    linkedin: 'https://www.linkedin.com/in/haleeyamaya/',
    website: 'https://www.instagram.com/haleeyamaya/',
    photo:
      'https://cdn.magicpatterns.com/uploads/n5qwq8JzRB6JoW9PrxHDGs/Haley_Amaya.png',
  },

  // ——— Community leaders ———
  {
    id: 'lilian-castro',
    name: 'Lilian Castro',
    title: 'Founder',
    organization: 'Garifuna Immersions Co.',
    location: 'Boston, MA',
    sector: 'Nonprofit',
    website: 'https://www.garifunaimmersionsco.com',
    photo:
      'https://cdn.magicpatterns.com/uploads/btbNXoHqkay4Uv9CVY7PTr/Lilian_Castro.png',
  },
  {
    id: 'vanessa-merren-taylor',
    name: 'Vanessa Merren-Taylor',
    placeAfter: 'jesenia-castro-burke',
    title:
      'Vice President, BHRC · Healthcare Revenue Cycle Professional',
    organization: 'Boston Honduran Reunion Committee (BHRC)',
    location: 'Boston, MA · Atlanta, GA',
    sector: 'Nonprofit',
    secondarySector: 'Health',
    photo:
      'https://cdn.magicpatterns.com/uploads/q89uZKqxiwEsuCdn6V7nxo/Vanessa_Merren_Taylor.png',
    linkedin: 'https://www.linkedin.com/in/venessa-merren-taylor-1b218139/',
  },
  {
    id: 'donald-osgood',
    name: 'Donald Osgood Sr.',
    title: 'Associate Pastor',
    organization: 'Wellspring Global Fellowship',
    location: 'Boston, MA',
    sector: 'Nonprofit',
    linkedin:
      'https://www.linkedin.com/in/donald-maurice-osgood-sr-06b84241/',
    photo:
      'https://cdn.magicpatterns.com/uploads/1trVsK9nCyb2zbmPPuKdrA/Donald_Osgood_Dr.png',
  },
  {
    id: 'cristina-munoz',
    name: 'Cristina Muñoz',
    title: 'Principal',
    organization: 'CKM Public Affairs & Communications',
    location: 'New York, NY',
    sector: 'Business',
    linkedin: 'https://www.linkedin.com/in/cristina-k-mu%C3%B1oz-mpa/',
    photo:
      'https://cdn.magicpatterns.com/uploads/2pxCqKE9LBPcPUHAqH4hPs/Cristina_Munoz.jpg',
  },
]
